import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { authApi, getErrorMessage } from '../services/api';
import { COLORS } from '@elevair/shared';

// Stricter email validation regex (requires local@domain.tld format)
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .refine(
      (email) => emailRegex.test(email.trim()),
      { message: 'Please enter a valid email address' }
    ),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await authApi.forgotPassword(data.email);
      setIsSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.background,
      }}
    >
      {/* Header */}
      <Box sx={{ py: 2, px: 3 }}>
        <Typography
          component={RouterLink}
          to="/"
          variant="h5"
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
            color: COLORS.primary,
            textDecoration: 'none',
          }}
        >
          Elevair
        </Typography>
      </Box>

      {/* Forgot Password Form */}
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 4 }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            {isSuccess ? (
              // Success State
              <Box textAlign="center">
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Check Your Email
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  If an account exists with that email address, we've sent you a link to reset your
                  password. The link will expire in 1 hour.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Didn't receive the email? Check your spam folder or try again.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Back to Sign In
                </Button>
              </Box>
            ) : (
              // Form State
              <>
                <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                  Forgot Password?
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mb: 4 }}
                >
                  Enter your email and we'll send you a link to reset your password.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                  <TextField
                    {...register('email')}
                    label="Email"
                    type="text"
                    fullWidth
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    autoComplete="email"
                    autoFocus
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isLoading}
                    sx={{ mt: 3, py: 1.5 }}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 3 }}
                  >
                    Remember your password?{' '}
                    <Link component={RouterLink} to="/login" underline="hover">
                      Sign in
                    </Link>
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
